import React, { useState } from 'react';
import { Bug, Zap, Settings, CheckCircle, Warehouse, Activity, ListOrdered, BarChart3, Barcode, Printer, Scan, Stethoscope } from 'lucide-react';
import { Card } from '../../../common/Card';
import { LoggingTest } from '../../../common/LoggingTest';
import { LocationActivityTest } from '../../../common/LocationActivityTest';
import { RuleGroupEngineTest } from './RuleGroupEngineTest';
import { WarehouseAPITest } from './WarehouseAPITest';
import { Fase3Test } from '../rack-builder-live/Fase3Test';
import { Fase5Test } from '../rack-builder-live/Fase5Test';
import Fase6Test from '../rack-builder-live/Fase6Test';
import Fase7Test from '../rack-builder-live/Fase7Test';
import Fase8Test from '../rack-builder-live/Fase8Test';
import { GS1TestComponent } from '../../../common/testing/GS1TestComponent';
import { GlobalScannerTest } from '../../../common/testing/GlobalScannerTest';
import { PrintTestComponent } from '../../../common/testing/PrintTestComponent';
import { PutAwayDiagnostics } from '../administration/PutAwayDiagnostics';

type TestTab = 'rulegroup' | 'logging' | 'warehouse' | 'signalr' | 'queue' | 'cache' | 'heatmap' | 'statistics' | 'settings' | 'gs1' | 'scanner' | 'print' | 'putaway-diag' | 'performance' | 'validation';

interface TestTabInfo {
  id: TestTab;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
  available: boolean;
}

export const TestModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TestTab>('rulegroup');

  const tabs: TestTabInfo[] = [
    {
      id: 'rulegroup',
      name: 'RuleGroupEngine',
      icon: Zap,
      description: 'Test systemregler og regelgrupper',
      available: true
    },
    {
      id: 'logging',
      name: 'Logging System',
      icon: Bug,
      description: 'Test logging funksjonalitet',
      available: true
    },
    {
      id: 'warehouse',
      name: 'Warehouse API',
      icon: Warehouse,
      description: 'Test warehouse endepunkter og data',
      available: true
    },
    {
      id: 'signalr',
      name: 'SignalR Activity',
      icon: Activity,
      description: 'Test sanntids lokasjonsaktivitet',
      available: true
    },
    {
      id: 'queue',
      name: 'Queue & Carousel',
      icon: ListOrdered,
      description: 'Test event queue og automatisk reol-bytte',
      available: true
    },
    {
      id: 'cache',
      name: 'Activity Cache',
      icon: Settings,
      description: 'Test event cache, persistence og midnight reset',
      available: true
    },
    {
      id: 'heatmap',
      name: 'Heatmap Viz',
      icon: BarChart3,
      description: 'Test heatmap visualisering og intensitet',
      available: true
    },
    {
      id: 'statistics',
      name: 'Statistics Panel',
      icon: Activity,
      description: 'Test statistikk, activity feed og top locations',
      available: true
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Settings,
      description: 'Test dashboard innstillinger og konfigurasjon',
      available: true
    },
    {
      id: 'gs1',
      name: 'GS1-128 Parser',
      icon: Barcode,
      description: 'Test GS1-128 strekkoder med ~ separator',
      available: true
    },
    {
      id: 'scanner',
      name: 'Global Scanner',
      icon: Scan,
      description: 'Test global strekkode-skanning uten tekstfelt',
      available: true
    },
    {
      id: 'print',
      name: 'Utskrift',
      icon: Printer,
      description: 'Test etikettutskrift og printerfunksjonalitet',
      available: true
    },
    {
      id: 'putaway-diag',
      name: 'Put-Away Diagnostikk',
      icon: Stethoscope,
      description: 'Diagnostiser og feilsøk vareplassering',
      available: true
    },
    {
      id: 'performance',
      name: 'Ytelse & Performance',
      icon: Settings,
      description: 'Test ytelse og performance overvåkning',
      available: false // Placeholder for future implementation
    },
    {
      id: 'validation',
      name: 'Data Validering',
      icon: CheckCircle,
      description: 'Test datavalidering og konsistens',
      available: false // Placeholder for future implementation
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'rulegroup':
        return <RuleGroupEngineTest />;
      case 'logging':
        return <LoggingTest />;
      case 'warehouse':
        return <WarehouseAPITest />;
      case 'signalr':
        return <LocationActivityTest />;
      case 'queue':
        return <Fase3Test />;
      case 'cache':
        return <Fase5Test />;
      case 'heatmap':
        return <Fase6Test />;
      case 'statistics':
        return <Fase7Test />;
      case 'settings':
        return <Fase8Test />;
      case 'gs1':
        return <GS1TestComponent />;
      case 'scanner':
        return <GlobalScannerTest />;
      case 'print':
        return <PrintTestComponent />;
      case 'putaway-diag':
        return <PutAwayDiagnostics onBack={() => setActiveTab('rulegroup')} />;
      case 'performance':
        return (
          <Card className="p-6 text-center">
            <Settings className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Ytelse & Performance</h3>
            <p className="text-gray-500">Denne funksjonaliteten er ikke implementert ennå.</p>
          </Card>
        );
      case 'validation':
        return (
          <Card className="p-6 text-center">
            <CheckCircle className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Data Validering</h3>
            <p className="text-gray-500">Denne funksjonaliteten er ikke implementert ennå.</p>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center gap-3">
          <Bug size={32} />
          <div>
            <h1 className="text-2xl font-bold">Test og Debugging</h1>
            <p className="text-blue-100">Test systemfunksjonalitet og feilsøking</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <Card className="p-1">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isAvailable = tab.available;

            return (
              <button
                key={tab.id}
                onClick={() => isAvailable && setActiveTab(tab.id)}
                disabled={!isAvailable}
                className={`
                  p-4 rounded-lg text-center transition-all duration-200 relative
                  ${isActive && isAvailable
                    ? 'bg-blue-500 text-white shadow-md'
                    : isAvailable
                      ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed opacity-60'
                  }
                `}
                title={isAvailable ? tab.description : 'Ikke tilgjengelig'}
              >
                <Icon size={24} className="mx-auto mb-2" />
                <div className="text-sm font-medium">{tab.name}</div>
                <div className="text-xs mt-1 opacity-75">{tab.description}</div>
                
                {!isAvailable && (
                  <div className="absolute top-2 right-2">
                    <span className="text-xs bg-gray-300 text-gray-600 px-2 py-1 rounded-full">
                      Kommer snart
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {renderTabContent()}
      </div>

      {/* Footer Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Bug className="text-blue-600 mt-1 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Test og Debugging Informasjon</p>
            <p className="mb-2">
              Denne seksjonen inneholder verktøy for testing av systemfunksjonalitet og feilsøking.
              Alle tester kjøres i debug-modus og logger aktivitet for analyse.
            </p>
            <div className="text-xs text-blue-600 space-y-1">
              <div>• <strong>RuleGroupEngine:</strong> Test systemregler og regelgrupper som styrer lagerprosessene</div>
              <div>• <strong>Logging System:</strong> Test frontend logging og feilrapportering</div>
              <div>• <strong>Warehouse API:</strong> Test warehouse endepunkter, autentisering og datahenting</div>
              <div>• <strong>SignalR Activity:</strong> Test sanntids lokasjonsaktivitet via WebSocket-tilkobling</div>
              <div>• <strong>Queue & Carousel:</strong> Test event queue, automatisk reol-bytte og LRU cache</div>
              <div>• <strong>Activity Cache:</strong> Test event cache, localStorage persistence og midnight reset</div>
              <div>• <strong>Heatmap Viz:</strong> Test heatmap visualisering, intensity calculation og color schemes</div>
              <div>• <strong>Statistics Panel:</strong> Test statistikkpanel, aktivitetsfeed og top lokasjoner</div>
              <div>• <strong>Settings:</strong> Test dashboard innstillinger og konfigurasjon</div>
              <div>• <strong>GS1-128 Parser:</strong> Test og massetest GS1-128 strekkoder med tilde (~) separator</div>
              <div>• <strong>Global Scanner:</strong> Test global strekkode-skanning med useGlobalBarcodeScanner hook</div>
              <div>• <strong>Utskrift:</strong> Test etikettutskrift med browser print og WebSocket (silent printing)</div>
              <div>• <strong>Put-Away Diagnostikk:</strong> Diagnostiser og feilsøk vareplassering med detaljert steg-for-steg analyse</div>
              <div>• <strong>Ytelse & Performance:</strong> Kommer snart - overvåkning av systemytelse</div>
              <div>• <strong>Data Validering:</strong> Kommer snart - validering av datakonsistens</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
